import { Button, Descriptions, Form, Row, Select, Space, Typography } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormatDate } from '../../../../constants';
import {
  StaffEditContainerStaffRoleFieldsFragment,
  StaffEditContainerStaffUserCurrentFieldsFragment,
  StaffEditContainerStaffUserFieldsFragment,
  StaffUserUpdateInput
} from '../../../../generated';

interface StaffEditProps {
  onSubmit: (payload: StaffUserUpdateInput) => Promise<void>;
  updateStaffLoading: boolean;
  data: StaffEditContainerStaffUserFieldsFragment;
  listOfRoles: StaffEditContainerStaffRoleFieldsFragment[];
  currentStaffUser: StaffEditContainerStaffUserCurrentFieldsFragment;
}

export const StaffEdit: React.FC<StaffEditProps> = ({ data, listOfRoles, onSubmit, updateStaffLoading, currentStaffUser }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const handleSubmit = async (values: StaffUserUpdateInput) => {
    // only send data that user has permission to update
    if (!currentStaffUser.role?.permissions.staffUserPermissions.canAssignRoles) {
      delete values.role;
    }
    if (!currentStaffUser.role?.permissions.staffUserPermissions.canBlockAccess) {
      delete values.accessBlocked;
    }
    if (!currentStaffUser.role?.permissions.staffUserPermissions.canAddOrRemoveTags) {
      delete values.tags;
    }

    await onSubmit(values);
  };

  const filteredRoles = React.useMemo(() => {  
    const initialRoles = listOfRoles ?? [];  
    return currentStaffUser.role?.roleSubtype !== 'Staff.IS'  
      ? initialRoles.filter((role) => role.roleName !== 'IS Admin')  
      : initialRoles;  
  }, [listOfRoles, currentStaffUser.role?.roleSubtype]);  
  const [roles] = React.useState<StaffEditContainerStaffRoleFieldsFragment[]>(filteredRoles ?? []);

  return (
    <div className="container">
      <Typography.Title level={5}>Staff Information</Typography.Title>
      <Descriptions
        column={3}
        size="small"
        layout="vertical"
        items={[
          {
            label: 'Id',
            children: data.id
          },
          {
            label: 'Created At',
            children: FormatDate(data.createdAt)
          },
          {
            label: 'Updated At',
            children: FormatDate(data.updatedAt)
          },
          {
            label: 'First Name',
            children: data.firstName
          },
          {
            label: 'Last Name',
            children: data.lastName
          }
        ]}
      />
      <Form
        form={form}
        layout="vertical"
        style={{
          marginTop: '20px'
        }}
        onFinish={handleSubmit}
        initialValues={data}
      >
        <Form.Item
          label="Role:"
          rules={[
            {
              required: true,
              message: 'Role is Required'
            }
          ]}
          name={['role', 'id']}
        >
          <Select style={{ width: 'auto', minWidth: 250 }} disabled={!currentStaffUser.role?.permissions.staffUserPermissions.canAssignRoles}>
            {roles?.map((role) => (
              <Select.Option key={role.id} value={role.id}>
                {role.roleName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Access Restricted:"
          rules={[
            {
              required: true,
              message: 'Access Restricted is Required'
            }
          ]}
          name={'accessBlocked'}
        >
          <Select style={{ width: 'auto' }} disabled={!currentStaffUser.role?.permissions.staffUserPermissions.canBlockAccess}>
            <Select.Option value={true}>Yes</Select.Option>
            <Select.Option value={false}>No</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label="Tags:" name={'tags'}>
          <Select
            disabled={!currentStaffUser.role?.permissions.staffUserPermissions.canAddOrRemoveTags}
            suffixIcon={null}
            open={false}
            mode="tags"
            placeholder="Type and press Enter to new tags..."
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Row style={{ width: '100%' }} justify={'end'}>
          <Space style={{ alignItems: 'baseline' }}>
            <Button onClick={() => navigate(-1)} type="default" disabled={updateStaffLoading}>
              Cancel
            </Button>
            <Form.Item>
              <Button loading={updateStaffLoading} type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
          </Space>
        </Row>
      </Form>
    </div>
  );
};
